import { taxService } from "../services/TaxService"

// Cached processed tax data
let cachedTaxesAux = null
let cachedBasesAux = null
let cachedAccountAux = null
let cachedDetailAux = null
let cachedNotRetAux = null
let cachedNotAplRetAux = null
let cachedRetBaseAux = null

/**
 * Process taxes into different formats (bases, accounts, details, etc.)
 * This function is called lazily when taxes are loaded
 */
function processTaxes(taxes) {
  const taxes_aux = []
  Object.keys(taxes).forEach(k => {
    const e = {...taxes[k]} // Clone to avoid mutations
    e['name'] = k
    taxes_aux.push(e)
  });

  const bases_aux = JSON.parse(JSON.stringify(taxes_aux))
  bases_aux.forEach(e => {
    e['name'] = 'base_'+e['name']
    e['text'] = 'BASE '+e['text']
  });

  const account_aux = JSON.parse(JSON.stringify(taxes_aux))
  account_aux.forEach(e => {
    e['name'] = 'account_'+e['name']
    e['text'] = 'Account '+e['text']
  });

  const detail_aux = JSON.parse(JSON.stringify(taxes_aux))
  detail_aux.forEach(e => {
    e['name'] = 'detail_'+e['name']
    e['text'] = 'Detail '+e['text']
  });

  const not_ret_aux = []
  JSON.parse(JSON.stringify(taxes_aux)).forEach(e => {
    if(!e['name'].includes('_re_')){
      e['name'] = 'not_ret_'+e['name']
      e['text'] = 'Not Ret '+e['text']
      not_ret_aux.push(e)
    }
  });

  const not_apl_ret_aux = []
  JSON.parse(JSON.stringify(taxes_aux)).forEach(e => {
    if(!e['name'].includes('_re_')){
      e['name'] = 'not_apl_ret_'+e['name']
      e['text'] = 'Not Apl Ret '+e['text']
      not_apl_ret_aux.push(e)
    }
  });

  const ret_base_aux = []
  JSON.parse(JSON.stringify(taxes_aux)).forEach(e => {
    if(!e['name'].includes('_re_')){
      e['name'] = 'ret_base_'+e['name']
      e['text'] = 'Ret Base '+e['text']
      ret_base_aux.push(e)
    }
  });

  return {
    taxes_aux,
    bases_aux,
    account_aux,
    detail_aux,
    not_ret_aux,
    not_apl_ret_aux,
    ret_base_aux
  }
}

/**
 * Initialize constants with taxes from backend
 * This should be called before using any tax-related constants
 */
async function initializeConstants() {
  if (cachedTaxesAux !== null) {
    return // Already initialized
  }

  const taxes = await taxService.getTaxes()
  const processed = processTaxes(taxes)

  cachedTaxesAux = processed.taxes_aux
  cachedBasesAux = processed.bases_aux
  cachedAccountAux = processed.account_aux
  cachedDetailAux = processed.detail_aux
  cachedNotRetAux = processed.not_ret_aux
  cachedNotAplRetAux = processed.not_apl_ret_aux
  cachedRetBaseAux = processed.ret_base_aux
}

/**
 * Get processed taxes synchronously (returns cached or defaults)
 */
function getTaxesAuxSync() {
  if (cachedTaxesAux) return cachedTaxesAux

  // Return defaults if not loaded yet
  const defaults = taxService.getDefaults()
  const processed = processTaxes(defaults)
  return processed.taxes_aux
}

/**
 * Async getters for each constant type
 */
async function getTaxesAux() {
  await initializeConstants()
  return cachedTaxesAux
}

async function getBasesAux() {
  await initializeConstants()
  return cachedBasesAux
}

async function getAccountAux() {
  await initializeConstants()
  return cachedAccountAux
}

async function getDetailAux() {
  await initializeConstants()
  return cachedDetailAux
}

async function getNotRetAux() {
  await initializeConstants()
  return cachedNotRetAux
}

async function getNotAplRetAux() {
  await initializeConstants()
  return cachedNotAplRetAux
}

async function getRetBaseAux() {
  await initializeConstants()
  return cachedRetBaseAux
}

// For backward compatibility - export sync versions using defaults
// These will use backend data once loaded
let taxes_aux = getTaxesAuxSync()
let bases_aux = cachedBasesAux || []
let account_aux = cachedAccountAux || []
let detail_aux = cachedDetailAux || []
let not_ret_aux = cachedNotRetAux || []
let not_apl_ret_aux = cachedNotAplRetAux || []
let ret_base_aux = cachedRetBaseAux || []

// Initialize with defaults immediately
const defaultTaxes = taxService.getDefaults()
const defaultProcessed = processTaxes(defaultTaxes)
taxes_aux = defaultProcessed.taxes_aux
bases_aux = defaultProcessed.bases_aux
account_aux = defaultProcessed.account_aux
detail_aux = defaultProcessed.detail_aux
not_ret_aux = defaultProcessed.not_ret_aux
not_apl_ret_aux = defaultProcessed.not_apl_ret_aux
ret_base_aux = defaultProcessed.ret_base_aux

const INFO_LABELS_1 = [
  { name: "inv_number", color: "red", text: "Num. fra" },
  { name: "supplier", color: "orange", text: "Proveedor" },
  { name: "vat_supplier", color: "orange", text: "CIF Proveedor" },
  { name: "customer", color: "orange", text: "Cliente" },
  { name: "vat_customer", color: "orange", text: "CIF Cliente" },
  //{name: "tax_type", color: "yellow", text:"Tax type"},
  //{name: "tax_amount", color: "green", text:"Tax amount"},
]

const INFO_LABELS_2 = [
  //{name: "base_amount", color: "purple", text:"Base amount"},
  { name: "total_amount", color: "pink", text: "Importe total" },
  { name: "invoice_date", color: "indigo", text: "Fecha fra" },
  { name: "operation_date", color: "indigo", text: "Fecha operación" },
  { name: "due_date", color: "lime", text: "Vencimiento" },
  { name: "payment_method", color: "teal", text: "Met. pago" },
  { name: "retention_type", color: "cyan", text: "Tipo retención" },
  { name: "retention_amount", color: "light-green", text: "Retención" },
  { name: "third_concept", color: "crimson", text: "Concepto" },
  { name: "other_amounts", color: "light-blue", text: "Suplidos" },
  { name: "subsidized_amount", color: "light-blue", text: "Importe subvencionado" },
  { name: "amount_exempt", color: "deep-purple", text: "Amount exempt(not in ba)" },//620
  { name: "amount_to_pay", color: "amber", text: "Amount to pay" },
  { name: "delivery_note_number", color: "#B0B0B0", text: "Número Albarán" },
  //{name: "phones", color: "#04D5DB", text:"Phones"},
  //{name: "phone_amounts", color: "#B91AD3", text:"Phones amounts"},
  { name: "background", color: "#CCCCCC", text: "Background" },]


export const INFO_LABELS = INFO_LABELS_1.concat(INFO_LABELS_2)

export const BASES_LABELS = bases_aux.concat([
  { name: "base_vat_4_5", color: "blue", text: "Base IVA+RE 4%+0.5%" },
  { name: "base_vat_5_625", color: "blue", text: "Base IVA+RE 5%+0.62%" },
  { name: "base_vat_11_4", color: "blue", text: "Base IVA+RE 10%+1.4%" },
  { name: "base_vat_26_2", color: "blue", text: "Base IVA+RE 21%+5.2%" },
  { name: "base_vat_75_1", color: "blue", text: "Base IVA+RE 7.5%+1%" },
  { name: "base_vat_2_026", color: "blue", text: "Base IVA+RE 2%+0.26%" },
  { name: "base_multi", color: "blue", text: "Multi Base" }
])

export const ACCOUNT_LABELS = account_aux.concat([
  { name: "account_vat_4_5", color: "blue", text: "Account IVA+RE 4%+0.5%" },
  { name: "account_vat_5_625", color: "blue", text: "Account IVA+RE 5%+0.62%" },
  { name: "account_vat_11_4", color: "blue", text: "Account IVA+RE 10%+1.4%" },
  { name: "account_vat_26_2", color: "blue", text: "Account IVA+RE 21%+5.2" },
  { name: "account_vat_75_1", color: "blue", text: "Account IVA+RE 7.5%+1%" },
  { name: "account_vat_2_026", color: "blue", text: "Account IVA+RE 2%+0.26%" },
  { name: "account_multi", color: "blue", text: "Multi Account" },
])

export const DETAIL_FIELDS = detail_aux.concat([
  { name: "detail_vat_4_5"},
  { name: "detail_vat_5_625"},
  { name: "detail_vat_11_4"},
  { name: "detail_vat_26_2"},
  { name: "detail_vat_75_1"},
  { name: "detail_vat_2_026"},
  { name: "detail_multi"},
])

export const TAXES_LABELS = taxes_aux.concat([
  { name: "vat_4_5", color: "blue", text: "IVA+RE 4%+0.5%" },
  { name: "vat_5_625", color: "blue", text: "IVA+RE 5%+0.62%" },
  { name: "vat_11_4", color: "blue", text: "IVA+RE 10%+1.4%" },
  { name: "vat_26_2", color: "blue", text: "IVA+RE 21%+5.2%" },
  { name: "vat_75_1", color: "blue", text: "IVA+RE 7.5%+1%" },
  { name: "vat_2_026", color: "blue", text: "IVA+RE 2%+0.26%" },
  { name: "inversion_sujeto_pasivo", color: "blue", text: "Inversión sujeto pasivo" },
  { name: "exemcion_igic_minorista", color: "blue", text: "Exención IGIC minorista" },
  { name: "line_code", color: "purple", text: "Código Línea" },
  { name: "line_amount", color: "yellow", text: "Precio unidad" },
  { name: "line_amount_base", color: "pink", text: "Precio base unidad" },
  { name: "line_quantity", color: "green", text: "Cantidad línea" },
  { name: "line_amount_total", color: "red", text: "Total línea" },
  { name: "line_description", color: "blue", text: "Descripción línea" },
  { name: "line_rate", color: "light-blue", text: "Prc impuesto" },
  { name: "line_tax", color: "deep-purple", text: "Cuota línea" },
  { name: "line_total_base", color: "cyan", text: "Base Total línea" },
  { name: "line_uom", color: "crimson", text: "Unidad de medida" },

])

export const INTRACOMUNITARIO_PERCENT_OPTIONS = [
  {text: 'IVA 21%', value: 'vat_21'},
  {text: 'IVA 12%', value: 'vat_12'},
  {text: 'IVA 10.5%', value: 'vat_10_5'},
  {text: 'IVA 10%', value: 'vat_10'},
  {text: 'IVA 7.5%', value: 'vat_75'},
  {text: 'IVA 4%', value: 'vat_4'},
  {text: 'IVA 2%', value: 'vat_2'},
  {text: 'IVA 0%', value: 'vat_0'},
  {text: 'IGIC 20%', value: 'igic_20'},
  {text: 'IGIC 15%', value: 'igic_15'},
  {text: 'IGIC 9.5%', value: 'igic_95'},
  {text: 'IGIC 7%', value: 'igic_7'},
  {text: 'IGIC 5%', value: 'igic_5'},
  {text: 'IGIC 3%', value: 'igic_3'},
  {text: 'IGIC 1%', value: 'igic_1'},
  {text: 'IGIC 0%', value: 'igic_0'}
]

export const INTRACOMUNITARIO_TYPE_OPTIONS = [
  {text: 'Bienes', value: 'bienes'},
  {text: 'Servicios', value: 'servicios'},
]

// Additional option for feature_add_intra_bienes_servicios
export const ADDITIONAL_INTRACOMUNITARIO_TYPE_OPTIONS = [
  {text: 'Bienes y Servicios', value: 'bienes_servicios'},
]

// Additional option for feature_add_intra_op_triangular
export const OP_TRIANGULAR_OPTIONS = [
  {text: 'OP. triangular', value: 'op_triangular'},
]

export function getIntracomunitarioTypeOptions(hasBienesServiciosFeature, hasOpTriangularFeature = false) {
  let options = [...INTRACOMUNITARIO_TYPE_OPTIONS]
  if (hasBienesServiciosFeature) {
    options = [...options, ...ADDITIONAL_INTRACOMUNITARIO_TYPE_OPTIONS]
  }
  if (hasOpTriangularFeature) {
    options = [...options, ...OP_TRIANGULAR_OPTIONS]
  }
  return options
}

export const INVERSION_SUJETO_PASIVO_IVA_PERCENT_OPTIONS = [
  {text: 'IVA 21%', value: 'vat_21'},
  {text: 'IVA 12%', value: 'vat_12'},
  {text: 'IVA 10.5%', value: 'vat_10_5'},
  {text: 'IVA 10%', value: 'vat_10'},
  {text: 'IVA 7.5%', value: 'vat_75'},
  {text: 'IVA 4%', value: 'vat_4'},
  {text: 'IVA 2%', value: 'vat_2'},
]

export const INVERSION_SUJETO_PASIVO_IGIC_PERCENT_OPTIONS = [
  {text: 'IGIC 1%', value: 'igic_1'},
  {text: 'IGIC 3%', value: 'igic_3'},
  {text: 'IGIC 5%', value: 'igic_5'},
  {text: 'IGIC 7%', value: 'igic_7'},
  {text: 'IGIC 9.5%', value: 'igic_95'},
  {text: 'IGIC 15%', value: 'igic_15'},
  {text: 'IGIC 20%', value: 'igic_20'}
]

// extracomunitario
export const IMPORTACIONES_PERCENT_OPTIONS = [
  {text: 'IVA 21%', value: 'vat_21'},
  {text: 'IVA 12%', value: 'vat_12'},
  {text: 'IVA 10.5%', value: 'vat_10_5'},
  {text: 'IVA 10%', value: 'vat_10'},
  {text: 'IVA 7.5%', value: 'vat_75'},
  {text: 'IVA 4%', value: 'vat_4'},
  {text: 'IVA 2%', value: 'vat_2'},
  {text: 'IVA 0%', value: 'vat_0'},
  {text: 'IGIC 20%', value: 'igic_20'},
  {text: 'IGIC 15%', value: 'igic_15'},
  {text: 'IGIC 9.5%', value: 'igic_95'},
  {text: 'IGIC 7%', value: 'igic_7'},
  {text: 'IGIC 5%', value: 'igic_5'},
  {text: 'IGIC 3%', value: 'igic_3'},
  {text: 'IGIC 1%', value: 'igic_1'},
  {text: 'IGIC 0%', value: 'igic_0'}
]

export const PROVIDER_SOMETHING_OPTIONS = {
  intracomunitario: INTRACOMUNITARIO_PERCENT_OPTIONS,
  inversion_sujeto_pasivo_iva: INVERSION_SUJETO_PASIVO_IVA_PERCENT_OPTIONS,
  inversion_sujeto_pasivo_igic: INVERSION_SUJETO_PASIVO_IGIC_PERCENT_OPTIONS,
  extracomunitario: IMPORTACIONES_PERCENT_OPTIONS,
  is_comerciante_minorista: INVERSION_SUJETO_PASIVO_IGIC_PERCENT_OPTIONS // Son las mismas opciones
}

export const PROVIDER_SOMETHING_OPTIONS2 = {
  intracomunitario: INTRACOMUNITARIO_TYPE_OPTIONS,
  extracomunitario: INTRACOMUNITARIO_TYPE_OPTIONS,
  foreign_entity: INTRACOMUNITARIO_TYPE_OPTIONS,
}

export function getProviderSomethingOptions2(hasBienesServiciosFeature, hasOpTriangularFeature = false) {
  const options = getIntracomunitarioTypeOptions(hasBienesServiciosFeature, hasOpTriangularFeature)
  return {
    intracomunitario: options,
    extracomunitario: options,
    foreign_entity: options,
  }
}

export const NOT_RET_CHECK = not_ret_aux

export const NOT_APL_RET = not_apl_ret_aux

export const BASE_RET = ret_base_aux

export const STANDARD_LABELS = INFO_LABELS_1.concat(BASES_LABELS).concat(TAXES_LABELS).concat(INFO_LABELS_2);

export const STANDARD_LABELS_CLIENT = STANDARD_LABELS.concat(ACCOUNT_LABELS).concat(DETAIL_FIELDS).concat(NOT_RET_CHECK).concat(NOT_APL_RET).concat(BASE_RET)

export const DEFAULT_ANNOTATION_LAYER = "human";


export const ACCOUNTING_DATE_OPTIONS = [{id:'invoice_date',text:'Fecha de Factura'},{id:'today',text:'Hoy'},{id:'configurated',text:'Fecha Configurable'}]

export const LOAD_PATH_DESTINATION_OPTIONS = [
  {id:'invoice',text:'Factura (Contabilidad)'},
  {id:'albaran',text:'Albarán'},
  {id:'factura',text:'Factura (Albaranes)'},
  {id:'pedido',text:'Pedidos'},
  {id:'document',text:'Documento (Gestor Documental)'},
]

export const getLoadPathDestinationOptions = ({isEmail, isOneDrive, canUseDocuments, canUseAlbaranes}) => {
  return LOAD_PATH_DESTINATION_OPTIONS.filter(option => {
    if (option.id === 'document') return isEmail && canUseDocuments
    // The pedidos Excel is read only from OneDrive, by the pedidos module itself.
    if (option.id === 'pedido') return Boolean(isOneDrive) && canUseAlbaranes
    if (['albaran', 'factura'].includes(option.id)) return canUseAlbaranes
    return true
  })
}

export const REGISTRO_DATE_OPTIONS = [{id:'today',text:'Hoy'},{id:'invoice_date',text:'Fecha de Factura'},{id:'configurated',text:'Fecha Configurable'}]

export const OTHER_DOCUMENT_TYPES = [
  {
    id: 'payroll',
    text: 'Nómina'
  }, {
    id: 'contract',
    text:'Contrato'
  }, {
    id: 'model_tax',
    text:'Modelos/Impuestos'
  }, {
    id: 'certificate',
    text:'Certificado'
  }, {
    id: 'bill',
    text:'Facturas'
  }, {
    id: 'other',
    text:'Otros'
  }
]

export const DOCUMENT_PERIOD_OPTIONS = [{id:'monthly',text:'Mensual'}, {id:'quarterly',text:'Trimestral'},{id:'annual',text:'Anual'}]
export const DOCUMENT_PERIOD_OPTIONS_MONTHLY = [
  {id:1,text:'Enero'},
  {id:2,text:'Febrero'},
  {id:3,text:'Marzo'},
  {id:4,text:'Abril'},
  {id:5,text:'Mayo'},
  {id:6,text:'Junio'},
  {id:7,text:'Julio'},
  {id:8,text:'Agosto'},
  {id:9,text:'Septiembre'},
  {id:10,text:'Octubre'},
  {id:11,text:'Nomviembre'},
  {id:12,text:'Diciembre'},
]
export const DOCUMENT_PERIOD_OPTIONS_QUARTERLY = [
  {id:'quarterly_1',text:'Primero'},
  {id:'quarterly_2',text:'Segundo'},
  {id:'quarterly_3',text:'Tercero'}
]

// Export initialization function for loading taxes from backend
export {
  initializeConstants,
  getTaxesAux,
  getBasesAux,
  getAccountAux,
  getDetailAux,
  getNotRetAux,
  getNotAplRetAux,
  getRetBaseAux
}
